"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Menu, Badge, Tooltip } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import lightLogo from "../../assets/images/medilink.svg";
import darkLogo from "../../assets/images/darkmedilink.svg";
import collapsedLogo from "../../assets/images/logomedilink.svg";
import ROUTES from "../../routes/patientRoutes/routePaths";
import {
  selectEffectiveUser,
  selectEffectiveUserId,
} from "../../sous-profile/redux/selectors/sousProfileSelectors";

import useSidebarIndicators from "../../notifications/hooks/useSidebarIndicators";

import "./Sidebar.css";

/* ===== Icon wrapper ===== */
const IconWrap = ({ children, className = "" }) => (
  <span className={`sb-icon ${className}`} aria-hidden>
    {children}
  </span>
);

/* ===== Indicateurs ===== */
// Sidebar réduite : point sur l'icône
const IndicatorIcon = ({ icon, count, collapsed }) =>
  collapsed && count ? (
    <Badge dot offset={[-2, 4]}>
      {icon}
    </Badge>
  ) : (
    icon
  );

// Sidebar ouverte : compteur à côté du label
// Sidebar ouverte : compteur affiché à droite du label (dans le flux, sans superposition)
const IndicatorLabel = ({ children, count, collapsed, a11yLabel }) =>
  !collapsed && count ? (
    <span className="sb-label-with-badge">
      <span className="sb-label-with-badge__text">{children}</span>
      <Badge
        count={count}
        overflowCount={99}
        className="sb-inline-badge"
        title={a11yLabel ? `${count} ${a11yLabel}` : String(count)}
      />
    </span>
  ) : (
    <>{children}</>
  );

/* ==== ICONES ==== */
const IHome = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <path d="M3 10.5 12 4l9 6.5" />
      <path d="M5 10v9h14v-9" />
      <path d="M9 19v-6h6v6" />
    </svg>
  </IconWrap>
);

const IProfile = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" />
      <path d="M5 19.5a7 7 0 0 1 14 0" />
    </svg>
  </IconWrap>
);

const ICalendar = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3 10h18" />
    </svg>
  </IconWrap>
);

const IMessage = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <path d="M4 6h16v10H8l-4 4V6Z" />
    </svg>
  </IconWrap>
);

const IDoctors = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24" aria-label="Doctors">
      <path d="M22 10L12 6 2 10l10 4 10-4Z" />
      <path d="M6 12v5c0 2 3.5 3 6 3s6-1 6-3v-5" />
      <path d="M12 6v4" />
    </svg>
  </IconWrap>
);

const IPlaces = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24" aria-label="Places">
      <path d="M12 21s7-5.2 7-12a7 7 0 0 0-14 0c0 6.8 7 12 7 12Z" />
      <circle cx="12" cy="9" r="2.4" />
    </svg>
  </IconWrap>
);

const IPharmacy = () => (
  <IconWrap className="sb-icon--pill">
    <svg viewBox="0 0 24 24">
      <g transform="rotate(-28 12 12)">
        <rect x="5" y="7" width="14" height="10" rx="5" />
        <line x1="12" y1="7" x2="12" y2="17" />
      </g>
    </svg>
  </IconWrap>
);

const IClinic = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24" aria-label="Clinics">
      <rect x="4" y="11" width="4" height="9" rx="1.8" />
      <rect x="16" y="11" width="4" height="9" rx="1.8" />
      <rect x="8" y="7" width="8" height="13" rx="3" />
      <path d="M12 9.5v2.5M10.75 10.75h2.5" />
      <path d="M10.5 20v-2.2a1.5 1.5 0 0 1 3 0V20" />
    </svg>
  </IconWrap>
);

const ILab = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <path d="M9 3v6l-4 7a3 3 0 0 0 2.6 4.5h8.8A3 3 0 0 0 19 16l-4-7V3" />
      <path d="M9 9h6" />
    </svg>
  </IconWrap>
);

const INurse = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24" aria-label="Nurses">
      <rect x="10" y="4" width="4" height="3" rx="1.2" />
      <rect x="6" y="6.5" width="12" height="13.5" rx="2.4" />
      <path d="M12 10.5v5.5M9.25 13.25h5.5" />
    </svg>
  </IconWrap>
);

const IPhysio = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24" aria-label="Physiotherapy">
      <path d="M3 12h18" />
      <path d="M6 12v6M12 12v6M18 12v6" />
      <path d="M5 20h2M11 20h2M17 20h2" />
      <circle cx="14" cy="6" r="1.6" />
      <path d="M13 7.8L11 12" />
      <path d="M12 9.6l2.4 1.4L16 12" />
      <path d="M11 12l-1.2 3.4" />
      <path d="M11.4 13.6l1.2 3.2" />
      <path d="M9 17.4h1.9M12.2 17.8h1.9" />
    </svg>
  </IconWrap>
);

const IPets = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24">
      <circle cx="6.5" cy="10" r="1.6" />
      <circle cx="10.5" cy="7.5" r="1.6" />
      <circle cx="13.5" cy="7.5" r="1.6" />
      <circle cx="17.5" cy="10" r="1.6" />
      <path d="M7 16c2.2-2.2 7.8-2.2 10 0 1.3 1.3.3 3-2 3H9c-2.3 0-3.3-1.7-2-3Z" />
    </svg>
  </IconWrap>
);

const IAssistant = () => (
  <IconWrap>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M21.2812 9.6375V9C21.2812 5.535 18.465 2.71875 15 2.71875H9C5.535 2.71875 2.71875 5.535 2.71875 9V9.6375C2.27625 9.87375 1.96875 10.335 1.96875 10.875V13.125C1.96875 13.9012 2.59875 14.5312 3.375 14.5312H4.21875V15C4.21875 16.395 5.355 17.5312 6.75 17.5312H17.25C18.645 17.5312 19.7812 16.395 19.7812 15V14.5312H20.625C21.09 14.5312 21.4688 14.1525 21.4688 13.6875V10.875C21.4688 10.41 21.09 10.0312 20.625 10.0312H19.7812V9C19.7812 7.0875 18.225 5.53125 16.3125 5.53125H7.6875C5.775 5.53125 4.21875 7.0875 4.21875 9V10.0312H3.375C2.91 10.0312 2.53125 10.41 2.53125 10.875V13.125C2.53125 13.59 2.91 13.9688 3.375 13.9688H4.21875"
        stroke="currentColor"
        strokeWidth="0.8"
      />
    </svg>
  </IconWrap>
);

const IPeriod = () => (
  <IconWrap className="sb-icon--period">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M12 15V22"
        stroke="#030303"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9 19H15"
        stroke="#030303"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 15C15.3137 15 18 12.3137 18 9C18 5.68629 15.3137 3 12 3C8.68629 3 6 5.68629 6 9C6 12.3137 8.68629 15 12 15Z"
        stroke="#030303"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </IconWrap>
);

const IWastebasket = () => (
  <IconWrap>
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3 6H21"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </IconWrap>
);

const IFamilyTree = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24" aria-label="Family Tree">
      <circle cx="12" cy="5" r="2" />
      <circle cx="5" cy="14" r="2" />
      <circle cx="12" cy="14" r="2" />
      <circle cx="19" cy="14" r="2" />
      <path d="M12 7v4M12 11H5v1M12 11h7v1" />
      <path d="M5 19v-2M12 19v-2M19 19v-2" />
      <circle cx="5" cy="20.5" r="1.5" />
      <circle cx="12" cy="20.5" r="1.5" />
      <circle cx="19" cy="20.5" r="1.5" />
    </svg>
  </IconWrap>
);

const IBilling = () => (
  <IconWrap>
    <svg viewBox="0 0 24 24" aria-label="Subscription">
      <rect x="2.5" y="5.5" width="19" height="13" rx="2.2" />
      <path d="M2.5 9.5h19" />
      <path d="M6 14.5h4" />
    </svg>
  </IconWrap>
);

/* ===== utils ===== */
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const p = value.split(`; ${name}=`);
  return p.length === 2 ? p.pop().split(";").shift() : undefined;
};

const resolveRoute = (route, userId) => {
  if (!route) return "";
  return typeof route === "function" ? route(userId) : String(route);
};

const baseOf = (path) =>
  String(path || "")
    .replace(/\/:\w+.*/g, "")
    .toLowerCase();

export default function SideBar({
  unreadMessages = 0,
  collapsed,
  onToggleCollapse,
  onMenuClick,
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const user = useSelector(selectEffectiveUser);
  const reduxUserId = useSelector(selectEffectiveUserId);
  const userGender = user?.gender;

  const cookieUserId =
    typeof document !== "undefined"
      ? getCookie("userId") || getCookie("id")
      : undefined;

  const userId = reduxUserId || cookieUserId;
  const pathname = (location.pathname || "").toLowerCase();

  const FEED = resolveRoute(ROUTES.FEED, userId) || `/feed/${userId}`;
  const OVERVIEWS = resolveRoute(ROUTES.OVERVIEWS, userId) || "/overviews";
  const MEDICAL_RECORD =
    resolveRoute(ROUTES.MEDICAL_RECORD, userId) || "/medicalrecord";
  const MEDICINES = resolveRoute(ROUTES.MEDICINES, userId) || "/medicines";
  const PROFILE = resolveRoute(ROUTES.PROFILE, userId) || `/profile/${userId}`;
  const PROFILE_EDIT =
    resolveRoute(ROUTES.PROFILE_EDIT, userId) || "/profile/edit";
  const APPOINTMENTS =
    resolveRoute(ROUTES.APPOINTMENTS, userId) || "/appointments";
  const MESSAGING = resolveRoute(ROUTES.MESSAGING, userId) || "/messaging";
  const DOCTORS = resolveRoute(ROUTES.DOCTORS, userId) || "/doctor-finder";

  const PHARMACY = resolveRoute(ROUTES.PHARMACY, userId) || "/pharmacy";
  const CLINIQUE = resolveRoute(ROUTES.CLINIQUE, userId) || "/clinique";
  const LAB = resolveRoute(ROUTES.LAB, userId) || "/lab";
  const INFIRMARIES =
    resolveRoute(ROUTES.INFIRMARIES, userId) || "/infirmaries";
  const KINE = resolveRoute(ROUTES.KINE, userId) || "/kine";

  const CYCLE = resolveRoute(ROUTES.CYCLE, userId) || "/cycle";
  const MEDICAL_ASSISTANT =
    resolveRoute(ROUTES.MedicalASSISTANT, userId) || "/medicalassistant";
  const WASTEBASKET = resolveRoute(ROUTES.WASTEBASKET, userId) || "/trash";
  const PAYMENT = resolveRoute(ROUTES.PAYMENT, userId) || "/payment";
  const FAMILY_TREE =
    resolveRoute(ROUTES.FAMILY_TREE, userId) || "/family-tree";


      // ===== Indicateurs d'événements (temps réel) =====
  const { counts } = useSidebarIndicators({
    role: "patient",
    routes: { messages: MESSAGING, appointments: APPOINTMENTS },
  });
  const msgCount = counts.messages || unreadMessages; // la prop reste un simple fallback
  const apptCount = counts.appointments || 0;

  

  const PROFILE_MENU_KEY = OVERVIEWS;
  const PLACES_MENU_KEY = "places-menu";

  const isInProfileGroup = useMemo(() => {
    const bases = [
      baseOf(OVERVIEWS),
      baseOf(MEDICAL_RECORD),
      baseOf(MEDICINES),
      baseOf(PROFILE),
      baseOf(PROFILE_EDIT),
      "/profile",
      "/medical-questionspage",
      "/records",
      "/medicalrecord",
    ].filter(Boolean);

    return bases.some((b) => pathname.startsWith(b));
  }, [pathname, OVERVIEWS, MEDICAL_RECORD, MEDICINES, PROFILE, PROFILE_EDIT]);

  const isInAppointmentsGroup = useMemo(() => {
    const prefixes = ["/appointments", "/appointment", "/doctor-details"];
    return prefixes.some((p) => pathname.startsWith(p));
  }, [pathname]);

  const isInPlacesGroup = useMemo(() => {
    const prefixes = [
      baseOf(PHARMACY),
      baseOf(CLINIQUE),
      baseOf(LAB),
      baseOf(INFIRMARIES),
      baseOf(KINE),
      "/pharmacy",
      "/clinique",
      "/clinic",
      "/clinics",
      "/lab",
      "/laboratory",
      "/infirmaries",
      "/nurses",
      "/kine",
    ].filter(Boolean);

    return prefixes.some((p) => pathname.startsWith(p));
  }, [pathname, PHARMACY, CLINIQUE, LAB, INFIRMARIES, KINE]);

  const isCyclePage = useMemo(
    () => pathname.startsWith(baseOf(CYCLE)) || pathname.startsWith("/cycle"),
    [pathname, CYCLE]
  );

  const [openKeys, setOpenKeys] = useState([]);

  useEffect(() => {
    if (collapsed) {
      setOpenKeys([]);
      return;
    }

    if (isInPlacesGroup) {
      setOpenKeys([PLACES_MENU_KEY]);
    }
  }, [collapsed, isInPlacesGroup]);

  const itemsTop = useMemo(() => {
    const placesLabel = t("navigation.places") || "Places";

    return [
      // MAIN
      {
        key: FEED,
        icon: <IHome />,
        label: t("navigation.home"),
        tooltip: t("navigation.home"),
      },
      {
        key: PROFILE_MENU_KEY,
        icon: <IProfile />,
        label: t("navigation.yourProfile"),
        tooltip: t("navigation.yourProfile"),
      },

      {
        type: "divider",
        className: "sb-menu-divider",
      },

      // CARE / APPOINTMENTS
      {
        key: DOCTORS,
        icon: <IDoctors />,
        label: t("navigation.doctors"),
        tooltip: t("navigation.doctors"),
      },
      // {
      //   key: APPOINTMENTS,
      //   icon: <ICalendar />,
      //   label: t("navigation.appointments"),
      //   tooltip: t("navigation.appointments"),
      // },
      // {
      //   key: MESSAGING,
      //   icon: <IMessage />,
      //   label: (
      //     <Badge count={unreadMessages} size="small" offset={[6, 0]}>
      //       {t("navigation.messages")}
      //     </Badge>
      //   ),
      //   tooltip: t("navigation.messages"),
      // },

            {
        key: APPOINTMENTS,
        icon: <ICalendar />,
        label: t("navigation.appointments"),
        tooltip: t("navigation.appointments"),
        badge: apptCount,
        badgeLabel: "nouveaux rendez-vous",
      },
      {
        key: MESSAGING,
        icon: <IMessage />,
        label: t("navigation.messages"),
        tooltip: t("navigation.messages"),
        badge: msgCount,
        badgeLabel: "messages non lus",
      },

      {
        type: "divider",
        className: "sb-menu-divider",
      },

      // MEDICAL / PERSONAL HEALTH
      ...(userGender === "female"
        ? [
            {
              key: CYCLE,
              icon: <IPeriod />,
              label: t("navigation.myPeriod"),
              tooltip: t("navigation.myPeriod"),
            },
          ]
        : []),

      {
        key: "/pets",
        icon: <IPets />,
        label: t("navigation.petsProfile"),
        tooltip: t("navigation.petsProfile"),
      },
      {
        key: FAMILY_TREE,
        icon: <IFamilyTree />,
        label: t("navigation.familyTree") || "Sous-profil",
        tooltip: t("navigation.familyTree") || "Sous-profil",
      },

      {
        type: "divider",
        className: "sb-menu-divider",
      },

      // PLACES
      {
        key: PLACES_MENU_KEY,
        icon: <IPlaces />,
        label: placesLabel,
        tooltip: placesLabel,
        children: [
          {
            key: PHARMACY,
            icon: <IPharmacy />,
            label: t("navigation.pharmacy"),
            tooltip: t("navigation.pharmacy"),
          },
          {
            key: CLINIQUE,
            icon: <IClinic />,
            label: t("navigation.clinics"),
            tooltip: t("navigation.clinics"),
          },
          {
            key: LAB,
            icon: <ILab />,
            label: t("navigation.analysisLab"),
            tooltip: t("navigation.analysisLab"),
          },
          {
            key: INFIRMARIES,
            icon: <INurse />,
            label: t("navigation.nurses"),
            tooltip: t("navigation.nurses"),
          },
          {
            key: KINE,
            icon: <IPhysio />,
            label: t("navigation.physiotherapy"),
            tooltip: t("navigation.physiotherapy"),
          },
        ],
      },

      {
        type: "divider",
        className: "sb-menu-divider",
      },

      // ASSISTANT / OTHER
      {
        key: MEDICAL_ASSISTANT,
        icon: <IAssistant />,
        label: t("navigation.aiMedicalAssistant"),
        tooltip: t("navigation.aiMedicalAssistant"),
      },
      {
        key: WASTEBASKET,
        icon: <IWastebasket />,
        label: t("navigation.trash") || "Trash",
        tooltip: t("navigation.trash") || "Trash",
      },
      {
        key: PAYMENT,
        icon: <IBilling />,
        label: t("navigation.subscription") || "Subscription",
        tooltip: t("navigation.subscription") || "Subscription",
      },
    ];
  }, [
    t,
    FEED,
    PROFILE_MENU_KEY,
    APPOINTMENTS,
    MESSAGING,
    // unreadMessages,
     msgCount,
    apptCount,
    DOCTORS,
    PHARMACY,
    CLINIQUE,
    LAB,
    INFIRMARIES,
    KINE,
    CYCLE,
    FAMILY_TREE,
    MEDICAL_ASSISTANT,
    WASTEBASKET,
    PAYMENT,
    userGender,
  ]);

  const selectedKey = useMemo(() => {
    if (isInAppointmentsGroup) return APPOINTMENTS;
    if (isInProfileGroup) return PROFILE_MENU_KEY;

    const flatItems = itemsTop.flatMap((item) => {
      if (item.type === "divider") return [];
      return item.children ? [item, ...item.children] : [item];
    });

    const exact = flatItems.find(
      (i) => String(i.key).toLowerCase() === pathname
    )?.key;

    if (exact) return exact;

    const byPrefix = flatItems.find((i) => {
      const b = baseOf(i.key);
      return b && pathname.startsWith(b);
    })?.key;

    return byPrefix || pathname;
  }, [
    isInAppointmentsGroup,
    APPOINTMENTS,
    isInProfileGroup,
    PROFILE_MENU_KEY,
    itemsTop,
    pathname,
  ]);

  const withMaybeTooltip = (text, node) =>
    collapsed ? (
      <Tooltip placement="right" title={text}>
        {node}
      </Tooltip>
    ) : (
      node
    );

  const buildMenuItem = (item) => {
    if (item.type === "divider") {
      return item;
    }

    // const labelNode = item.disabled ? (
    //   <span className="sb__item--disabled">{item.label}</span>
    // ) : (
    //   withMaybeTooltip(
    //     item.tooltip || (typeof item.label === "string" ? item.label : ""),
    //     <span>{item.label}</span>
    //   )
    // );

    // return {
    //   key: item.key,
    //   icon: item.icon,
    //   disabled: item.disabled,
    //   label: labelNode,
    //   className: selectedKey === item.key ? "menu-item-selected" : "",
    //   children: item.children?.map(buildMenuItem),
    // };

        const labelNode = item.disabled ? (
      <span className="sb__item--disabled">{item.label}</span>
    ) : (
      withMaybeTooltip(
        item.tooltip || (typeof item.label === "string" ? item.label : ""),
        <span>
          <IndicatorLabel
            count={item.badge}
            collapsed={collapsed}
            a11yLabel={item.badgeLabel}
          >
            {item.label}
          </IndicatorLabel>
        </span>
      )
    );

    return {
      key: item.key,
      icon: (
        <IndicatorIcon
          icon={item.icon}
          count={item.badge}
          collapsed={collapsed}
        />
      ),
      disabled: item.disabled,
      label: labelNode,
      className: selectedKey === item.key ? "menu-item-selected" : "",
      children: item.children?.map(buildMenuItem),
    };
  };

  const handleMenuClick = ({ key }) => {
    if (!key) return;

    if (key === PLACES_MENU_KEY) return;

    navigate(key);

    if (onMenuClick) {
      onMenuClick();
    }
  };

  return (
    <aside
      className={`sb ${collapsed ? "sb--collapsed" : ""} ${
        isCyclePage ? "sb--cycle" : ""
      }`}
    >
      <div className="sb__header">
        {collapsed ? (
          <img src={collapsedLogo} alt="Medilink" className="sb__logo sb__logo--collapsed" />
        ) : (
          <>
            <img src={lightLogo} alt="Medilink" className="sb__logo sb__logo--light" />
            <img src={darkLogo} alt="Medilink" className="sb__logo sb__logo--dark" />
          </>
        )}
      </div>

      <Menu
        className="sb__menu sb__menu--top"
        mode="inline"
        inlineCollapsed={collapsed}
        selectedKeys={[selectedKey]}
        openKeys={collapsed ? undefined : openKeys}
        onOpenChange={(keys) => setOpenKeys(keys)}
        onClick={handleMenuClick}
        items={itemsTop.map(buildMenuItem)}
      />
    </aside>
  );
}